import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { LuLoader } from "react-icons/lu";

import { CardCategoryEnum } from "@sorcery/db";
import { Line, P } from "@sorcery/ui";
import { deckbuildingRuleViolations } from "@sorcery/utils/universal/deck";

import { CardSquiggly } from "~/components/molecules/card/CardSquiggly";
import { DeckCardCount } from "~/components/molecules/deck/DeckCardCount";
import { DeckCollectionToolbar } from "~/components/molecules/deck/DeckCollectionToolbar";
import { DeckQuickAddInput } from "~/components/molecules/deck/DeckQuickAddInput";
import { DeckHistory } from "~/components/organisms/deck/decklist/DeckHistory";
import { Decklist } from "~/components/organisms/deck/decklist/Decklist";
import { STARTING_SITE_COUNT, STARTING_SPELL_COUNT } from "~/providers/SampleHandProvider";
import { useUser } from "~/providers/UserProvider";
import { avatarJotai, deckJotai, decklistJotai, sideboardJotai } from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import { Stats } from "../../../../../components/molecules/deck/Stats";
import { Comments } from "./Comments";
import { HandSimulator } from "./HandSimulator/HandSimulator";

export const ViewTab = ({ isAuthor }: { isAuthor: boolean }) => {
  /************************************************************************************
   * initialization
   */

  const { user } = useUser();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const sideboard = useAtomValue(sideboardJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const avatar = useAtomValue(avatarJotai)!;
  const [squiggleCard, setSquiggleCard] = useState<{
    card: CardType;
    variant: VariantType;
  }>({
    card: avatar.card,
    variant: avatar.variant,
  });

  /************************************************************************************
   * react helpers
   */
  const spellbookCount = useMemo(
    () =>
      decklist
        .filter((dc) => {
          return dc.card.guardian?.category === CardCategoryEnum.Spell;
        })
        .reduce((a, c) => a + Number(c.quantity), 0),
    [decklist],
  );
  const atlasCount = useMemo(
    () =>
      decklist
        .filter((dc) => {
          return dc.card.guardian?.category === CardCategoryEnum.Site;
        })
        .reduce((a, c) => a + Number(c.quantity), 0),
    [decklist],
  );
  const ruleInfringements = useMemo(
    () =>
      deckbuildingRuleViolations.filter(
        (r) =>
          deck.format === r.format &&
          r.check({
            decklist,
            sideboard,
          }),
      ),
    [deck.format, decklist, sideboard],
  );

  /************************************************************************************
   * render
   */
  return (
    <>
      <div className="animate-in fade-in">
        <div className="mb-8 flex flex-row">
          {/* Deck */}
          <div className="relative grow">
            {/* If author, show extra options */}
            <div className="mb-4">
              {!!user && <DeckCollectionToolbar />}
              {isAuthor && <DeckQuickAddInput />}
            </div>

            {/* Decklist */}
            <Decklist onCardHover={setSquiggleCard} size={"lg"} />

            <DeckHistory onCardHover={setSquiggleCard} />

            {!!ruleInfringements.length && (
              <div className="text-bold mb-4 border-t border-t-line-color py-5" role="alert">
                <P className="!mb-2 font-bold">
                  {" "}
                  <LuLoader size={11} className="-mt-[3px] mr-1 inline" />
                  <span>&nbsp;Work in progress</span>
                </P>

                {ruleInfringements.map((r) => (
                  <div key={r.name}>
                    <P className="!mb-0 italic">
                      <span>-&nbsp;</span>
                      {r.name}
                      {!!r.context && (
                        <em>
                          :{" "}
                          {r.context({
                            decklist,
                            sideboard,
                          })}
                        </em>
                      )}
                    </P>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="hidden pl-6 md:block">
            <CardSquiggly card={squiggleCard.card} variant={squiggleCard.variant} />
          </div>
        </div>

        <Line className="mb-8" />

        <div className="mb-6 flex justify-center">
          <DeckCardCount />
        </div>
        <Stats />

        {/* Hand simulator */}
        {(spellbookCount ?? 0) >= STARTING_SPELL_COUNT &&
          (atlasCount ?? 0) >= STARTING_SITE_COUNT && <HandSimulator />}

        <Line className="mb-8" />

        <Comments />
      </div>
    </>
  );
};
